import { useMemo, useState, type ReactNode } from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Pencil, Plus, RefreshCw, ShieldOff } from "lucide-react";

import { DataTable, type DataTableColumn, type DataTableSort } from "@/components/cinch/DataTable/DataTable";
import { type DateRange } from "@/components/cinch/DatePicker";
import { FilterBar, FilterClearAll } from "@/components/cinch/FilterBar";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { useAuthState } from "@/lib/auth/useUser";
import { useCan } from "@/lib/auth/useCan";
import { cn } from "@/lib/utils";

import { fmtCur, fmtDate, relativeAgo } from "../../renewals/format";
import {
  fetchAccountsEnrichment,
  useAccounts,
  useAccountsEnrichment,
  useCompanies,
  useContractTypes,
  useContracts,
  useDiscountWrites,
  useDiscounts,
  useItemWrites,
  useItems,
  usePackageWrites,
  usePackages,
  usePriceMetrics,
  useQuickbooks,
  type AccountRow,
  type CompanyRow,
  type ContractRow,
  type ContractTypeRow,
  type DiscountRow,
  type ItemRow,
  type ListParams,
  type PackageRow,
  type PriceMetricRow,
  type QbCustomerRow,
} from "../api";
import {
  ARCHIVE_STATUS_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
  ITEM_CATEGORY_OPTIONS,
  categoryLabel,
  discountTypeLabel,
  discountValueLabel,
  integrationLabels,
  marginPct,
  pricingTypeLabel,
} from "../catalog";
import {
  ACTIVE_STATUS_OPTIONS,
  DateRangeChip,
  MultiSelectChip,
  PAYMENT_OPTIONS,
  endOfDayIso,
  parseDate,
  startOfDayIso,
} from "../components/filters";
import { ContractEditor } from "../components/ContractEditor";
import { DiscountEditor } from "../components/DiscountEditor";
import { ItemEditor } from "../components/ItemEditor";
import { PackageEditor } from "../components/PackageEditor";
import { RecordDrawer, type Selection } from "../components/RecordDrawer";
import type { ContractsSearch, ContractsTabKey, DetailKind } from "..";

/** Passed to each tab. Convention (matches the rest of Ops): a row click opens
 *  the QUICK view; a name-cell click opens the EXPANDED view (opts.expanded). */
interface TabNav {
  openDetail: (kind: DetailKind, id: string, opts?: { expanded?: boolean }) => void; // URL-driven
  openRow: (sel: Selection, opts?: { expanded?: boolean }) => void; // quickbooks/items/price-metrics → local
  selectedId: string | null;
}

/** Name-cell button: opens the EXPANDED view. stopPropagation so the row's own
 *  click handler (which opens the QUICK view) doesn't also fire. */
function NameCell({ label, onExpand }: { label: ReactNode; onExpand: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onExpand();
      }}
      className="text-left font-medium text-brand hover:underline focus-visible:underline focus-visible:outline-none"
    >
      {label}
    </button>
  );
}

const TABS: { key: ContractsTabKey; label: string }[] = [
  { key: "contracts", label: "Contracts" },
  { key: "accounts", label: "Accounts" },
  { key: "companies", label: "Companies" },
  { key: "quickbooks", label: "QuickBooks" },
  { key: "items", label: "Items" },
  { key: "packages", label: "Packages" },
  { key: "discounts", label: "Discounts" },
  { key: "contract-types", label: "Contract Types" },
  { key: "price-metrics", label: "Price Metrics" },
];

const PAGE_SIZE = 20;
const asRow = (r: object): Record<string, unknown> => r as Record<string, unknown>;

export function ContractsPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/contracts" }) as ContractsSearch;
  const { user } = useAuthState();
  // Leaf-tab details (quickbooks/items/contract-types/price-metrics) are local —
  // they don't cross-navigate, so they don't need URL/history.
  const [rowSel, setRowSel] = useState<Selection | null>(null);

  const tab: ContractsTabKey = search.tab ?? "contracts";
  const expanded = !!search.expanded;

  // contracts/accounts/companies live in the URL so the browser back button
  // steps through contract → account → company navigation.
  const urlSel = useMemo<Selection | null>(() => {
    if (!search.detail) return null;
    const idx = search.detail.indexOf(":");
    if (idx < 0) return null;
    const kind = search.detail.slice(0, idx);
    const id = search.detail.slice(idx + 1);
    if (!id || !["contracts", "accounts", "companies", "contract-types"].includes(kind)) return null;
    return { kind: kind as ContractsTabKey, id, row: {} };
  }, [search.detail]);

  const selection = urlSel ?? rowSel;

  const go = (next: Partial<ContractsSearch>, replace = false) =>
    navigate({ to: "/contracts", search: (prev: ContractsSearch) => ({ ...prev, ...next }), replace });

  const openDetail = (kind: DetailKind, id: string, opts?: { expanded?: boolean }) => {
    setRowSel(null);
    // push → back-button friendly; name-cell click passes expanded:true.
    go({ detail: `${kind}:${id}`, expanded: opts?.expanded ? true : undefined });
  };
  const openRow = (sel: Selection, opts?: { expanded?: boolean }) => {
    // Local-detail kinds keep the row in state; expand still rides the URL so
    // the drawer width responds and the expand toggle has something to flip.
    go({ detail: undefined, expanded: opts?.expanded ? true : undefined });
    setRowSel(sel);
  };
  const closeDrawer = () => {
    if (urlSel || search.expanded) go({ detail: undefined, expanded: undefined });
    setRowSel(null);
  };
  // Expand/collapse REPLACES (no history entry) so back moves between records,
  // not between expand states.
  const setExpanded = (v: boolean) => go({ expanded: v ? true : undefined }, true);
  const setTab = (next: ContractsTabKey) => {
    setRowSel(null);
    go({ tab: next, detail: undefined, expanded: undefined });
  };

  const nav: TabNav = { openDetail, openRow, selectedId: selection?.id ?? null };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Contracts</h1>
        <p className="text-sm text-muted-foreground">
          The Cinch billing record — contracts, accounts, companies, QuickBooks, items, packages,
          discounts, contract types, and price metrics. A mirror of the Employee Portal billing
          section.
        </p>
      </div>

      {user && !user.billing_access ? (
        <NoBillingAccess />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as ContractsTabKey)}>
          <TabsList variant="underline">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="contracts">
            <ContractsTab nav={nav} />
          </TabsContent>
          <TabsContent value="accounts">
            <AccountsTab nav={nav} />
          </TabsContent>
          <TabsContent value="companies">
            <CompaniesTab nav={nav} />
          </TabsContent>
          <TabsContent value="quickbooks">
            <QuickBooksTab nav={nav} />
          </TabsContent>
          <TabsContent value="items">
            <ItemsTab nav={nav} />
          </TabsContent>
          <TabsContent value="packages">
            <PackagesTab nav={nav} />
          </TabsContent>
          <TabsContent value="discounts">
            <DiscountsTab nav={nav} />
          </TabsContent>
          <TabsContent value="contract-types">
            <ContractTypesTab nav={nav} />
          </TabsContent>
          <TabsContent value="price-metrics">
            <PriceMetricsTab nav={nav} />
          </TabsContent>
        </Tabs>
      )}

      <RecordDrawer
        selection={selection}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onClose={closeDrawer}
        onOpenDetail={openDetail}
      />
    </div>
  );
}

function NoBillingAccess() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
        <ShieldOff className="h-8 w-8 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Cinch billing access required</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          You can open this page in Cinch Ops, but viewing billing data also requires billing access
          in Cinch itself. Ask an admin to grant it there.
        </p>
      </CardContent>
    </Card>
  );
}

// ── shared per-tab state ─────────────────────────────────────────────────────
function useTableState(defaultSort: DataTableSort | null) {
  const [offset, setOffset] = useState(0);
  const [sort, setSortRaw] = useState<DataTableSort | null>(defaultSort);
  const [search, setSearchRaw] = useState("");
  // Backend filter params (param-name → value). Multi-selects are comma-joined
  // to match Cinch's django BaseInFilter (e.g. payment_type_in=CC,ACH).
  const [filters, setFiltersState] = useState<Record<string, string>>({});
  const setSort = (s: DataTableSort | null) => {
    setSortRaw(s);
    setOffset(0);
  };
  const setSearch = (v: string) => {
    setSearchRaw(v);
    setOffset(0);
  };
  // Merge a partial set of filter params; empty/undefined values clear that key.
  // Resets the page so a filter never lands the user on an out-of-range offset.
  const setFilters = (partial: Record<string, string | undefined>) => {
    setFiltersState((cur) => {
      const next = { ...cur };
      for (const [k, v] of Object.entries(partial)) {
        if (v == null || v === "") delete next[k];
        else next[k] = v;
      }
      return next;
    });
    setOffset(0);
  };
  const clearFilters = () => {
    setFiltersState({});
    setOffset(0);
  };
  const params: ListParams = {
    limit: PAGE_SIZE,
    offset,
    ...(sort ? { ordering: (sort.dir === "desc" ? "-" : "") + sort.key } : {}),
    ...(search ? { search } : {}),
    ...filters,
  };
  return {
    limit: PAGE_SIZE,
    offset,
    setOffset,
    sort,
    setSort,
    search,
    setSearch,
    filters,
    setFilters,
    clearFilters,
    params,
  };
}

function errMsg(e: unknown): string | null {
  if (!e) return null;
  if (e instanceof ApiError) return `${e.status} — ${e.message}`;
  return e instanceof Error ? e.message : "Request failed";
}

// ── tabs ─────────────────────────────────────────────────────────────────────
function ContractsTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "sign_date", dir: "desc" });
  const q = useContracts(st.params);
  const canWrite = useCan("contracts.write");
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  // Options for the Contract Type filter come from the contract-types endpoint.
  const typesQ = useContractTypes({ limit: 500, offset: 0 });
  const typeOptions = (typesQ.data?.results ?? []).map((t) => ({ value: t.id, label: t.name || t.id }));
  const f = st.filters;
  const contractType = f.contract_type ? f.contract_type.split(",").filter(Boolean) : [];
  const payment = f.payment_type_in ? f.payment_type_in.split(",").filter(Boolean) : [];
  const signRange: DateRange = { start: parseDate(f.sign_date_after), end: parseDate(f.sign_date_before) };
  const renewalRange: DateRange = {
    start: parseDate(f.renewal_date_after),
    end: parseDate(f.renewal_date_before),
  };
  const columns: DataTableColumn<ContractRow>[] = [
    {
      key: "company_name",
      header: "Company",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.company_name || "—"}
          onExpand={() => nav.openDetail("contracts", r.id, { expanded: true })}
        />
      ),
    },
    { key: "company_dba", header: "DBA", sortable: true, render: (r) => r.company_dba || "—" },
    {
      key: "contract_type__name",
      header: "Contract Type",
      sortable: true,
      render: (r) => r.contract_type_name || "—",
    },
    { key: "mrr_total", header: "MRR", numeric: true, sortable: true, render: (r) => fmtCur(r.mrr_total) },
    { key: "arr", header: "ARR", numeric: true, render: (r) => fmtCur((r.mrr_total ?? 0) * 12) },
    {
      key: "term_months",
      header: "Term",
      sortable: true,
      render: (r) => (r.term_months ? `${r.term_months} months` : "months"),
    },
    { key: "payment_type", header: "Payment", sortable: true, render: (r) => r.payment_type || "—" },
    { key: "sign_date", header: "Sign Date", sortable: true, render: (r) => fmtDate(r.sign_date) },
    { key: "renewal_date", header: "Renewal", sortable: true, render: (r) => fmtDate(r.renewal_date) },
  ];
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => setEditing(r.id)}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    });
  }
  return (
    <div className="space-y-3">
      <FilterBar>
        <MultiSelectChip
          label="Contract Type"
          options={typeOptions}
          value={contractType}
          onChange={(v) => st.setFilters({ contract_type: v.join(",") })}
        />
        <DateRangeChip
          label="Sign Date"
          value={signRange}
          onChange={(r) =>
            st.setFilters({ sign_date_after: startOfDayIso(r.start), sign_date_before: endOfDayIso(r.end) })
          }
        />
        <MultiSelectChip
          label="Payment Method"
          options={PAYMENT_OPTIONS}
          value={payment}
          onChange={(v) => st.setFilters({ payment_type_in: v.join(",") })}
        />
        <DateRangeChip
          label="Renewal Date"
          value={renewalRange}
          onChange={(r) =>
            st.setFilters({
              renewal_date_after: startOfDayIso(r.start),
              renewal_date_before: endOfDayIso(r.end),
            })
          }
        />
        <div className="flex-1" />
        {Object.keys(f).length > 0 && <FilterClearAll onClick={st.clearFilters} />}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={q.data?.results ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav.openDetail("contracts", r.id)}
        selectedKey={nav.selectedId}
        sort={st.sort}
        onSortChange={st.setSort}
        search={st.search}
        onSearchChange={st.setSearch}
        searchPlaceholder="Search company, contact, deal id…"
        pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
        onPageChange={st.setOffset}
        loading={q.isLoading}
        error={errMsg(q.error)}
        emptyMessage="No contracts."
        toolbar={canWrite ? <NewButton label="New Contract" onClick={() => setEditing(null)} /> : undefined}
      />
      <ContractEditor
        open={editing !== undefined}
        contractId={editing ?? null}
        onClose={() => setEditing(undefined)}
      />
    </div>
  );
}

function AccountsTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = useAccounts(st.params);
  const enr = useAccountsEnrichment();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const enrichment = enr.data?.accounts ?? {};
  const enrLoading = enr.isLoading;
  const f = st.filters;
  const activeStatus = f.active_status_in ? f.active_status_in.split(",").filter(Boolean) : [];
  const createdRange: DateRange = { start: parseDate(f.created_after), end: parseDate(f.created_before) };
  const updatedRange: DateRange = { start: parseDate(f.updated_after), end: parseDate(f.updated_before) };

  async function refresh() {
    setRefreshing(true);
    try {
      const data = await fetchAccountsEnrichment(true);
      qc.setQueryData(["contracts", "accounts", "enrichment"], data);
    } finally {
      setRefreshing(false);
    }
  }

  const columns: DataTableColumn<AccountRow>[] = [
    {
      key: "name",
      header: "Account Name",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openDetail("accounts", r.id, { expanded: true })}
        />
      ),
    },
    { key: "quickbooks_id", header: "QuickBooks ID", sortable: true, render: (r) => r.quickbooks_id ?? "—" },
    {
      key: "stripe",
      header: "Stripe ID(s)",
      render: (r) => {
        if (enrLoading) return <Skeleton className="h-4 w-24" />;
        const ids = enrichment[r.id]?.stripe_customer_ids ?? [];
        return ids.length ? ids.join(", ") : "—";
      },
    },
    { key: "created_when", header: "Created", sortable: true, render: (r) => fmtDate(r.created_when) },
    { key: "updated_when", header: "Updated", sortable: true, render: (r) => fmtDate(r.updated_when) },
    {
      key: "status",
      header: "Status",
      render: (r) => {
        if (enrLoading) return <Skeleton className="h-5 w-16" />;
        return <StatusBadge value={enrichment[r.id]?.is_active ? "active" : "inactive"} />;
      },
    },
  ];

  const toolbar = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {enr.data?.refreshed_at ? <span>Stripe/Status as of {relativeAgo(enr.data.refreshed_at)}</span> : null}
      <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
        <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
        Refresh
      </Button>
    </div>
  );

  return (
    <div className="space-y-3">
      <FilterBar>
        <MultiSelectChip
          label="Active Status"
          options={ACTIVE_STATUS_OPTIONS}
          value={activeStatus}
          onChange={(v) => st.setFilters({ active_status_in: v.join(",") })}
        />
        <DateRangeChip
          label="Created Date"
          value={createdRange}
          onChange={(r) =>
            st.setFilters({ created_after: startOfDayIso(r.start), created_before: endOfDayIso(r.end) })
          }
        />
        <DateRangeChip
          label="Updated Date"
          value={updatedRange}
          onChange={(r) =>
            st.setFilters({ updated_after: startOfDayIso(r.start), updated_before: endOfDayIso(r.end) })
          }
        />
        <div className="flex-1" />
        {Object.keys(f).length > 0 && <FilterClearAll onClick={st.clearFilters} />}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={q.data?.results ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav.openDetail("accounts", r.id)}
        selectedKey={nav.selectedId}
        sort={st.sort}
        onSortChange={st.setSort}
        search={st.search}
        onSearchChange={st.setSearch}
        searchPlaceholder="Search name or QuickBooks ID…"
        pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
        onPageChange={st.setOffset}
        loading={q.isLoading}
        error={errMsg(q.error)}
        emptyMessage="No accounts."
        toolbar={toolbar}
      />
    </div>
  );
}

function CompaniesTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = useCompanies(st.params);
  const columns: DataTableColumn<CompanyRow>[] = [
    {
      key: "name",
      header: "Company",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openDetail("companies", r.id, { expanded: true })}
        />
      ),
    },
    { key: "account_name", header: "Account", render: (r) => r.account_name || "—" },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge value={r.is_active ? "active" : "inactive"} />,
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={q.data?.results ?? []}
      rowKey={(r) => r.id}
      onRowClick={(r) => nav.openDetail("companies", r.id)}
      selectedKey={nav.selectedId}
      sort={st.sort}
      onSortChange={st.setSort}
      search={st.search}
      onSearchChange={st.setSearch}
      searchPlaceholder="Search companies…"
      pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
      onPageChange={st.setOffset}
      loading={q.isLoading}
      error={errMsg(q.error)}
      emptyMessage="No companies."
    />
  );
}

function QuickBooksTab({ nav }: { nav: TabNav }) {
  const st = useTableState(null);
  const q = useQuickbooks(st.params);
  const columns: DataTableColumn<QbCustomerRow>[] = [
    {
      key: "CompanyName",
      header: "Company",
      render: (r) => (
        <NameCell
          label={r.CompanyName || "—"}
          onExpand={() => nav.openRow({ kind: "quickbooks", id: r.Id, row: asRow(r) }, { expanded: true })}
        />
      ),
    },
    {
      key: "contact",
      header: "Contact",
      render: (r) => [r.GivenName, r.FamilyName].filter(Boolean).join(" ") || "—",
    },
    { key: "PrimaryEmailAddr", header: "Email", render: (r) => r.PrimaryEmailAddr || "—" },
    { key: "account", header: "Account", render: (r) => r.account?.name || "—" },
    { key: "Id", header: "QuickBooks ID", render: (r) => r.Id },
  ];
  return (
    <DataTable
      columns={columns}
      rows={q.data?.results ?? []}
      rowKey={(r) => r.Id}
      onRowClick={(r) => nav.openRow({ kind: "quickbooks", id: r.Id, row: asRow(r) })}
      selectedKey={nav.selectedId}
      search={st.search}
      onSearchChange={st.setSearch}
      searchPlaceholder="Search QuickBooks customers…"
      pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
      onPageChange={st.setOffset}
      loading={q.isLoading}
      error={errMsg(q.error)}
      emptyMessage="No QuickBooks customers."
    />
  );
}

function ItemsTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = useItems(st.params);
  const canWrite = useCan("contracts.write");
  // undefined = closed; null = create; ItemRow = edit.
  const [editing, setEditing] = useState<ItemRow | null | undefined>(undefined);
  const { archive } = useItemWrites();
  // Price Metric filter options come from the price-metrics endpoint.
  const metricsQ = usePriceMetrics({ limit: 500, offset: 0 });
  const metricOptions = (metricsQ.data?.results ?? []).map((m) => ({ value: m.id, label: m.name || m.id }));
  const f = st.filters;
  // Cinch's Item filter is `category_in` (comma-separated), not the single-value
  // `category` (which is iexact and matches nothing for a multi-value selection).
  const category = f.category_in ? f.category_in.split(",").filter(Boolean) : [];
  const priceMetric = f.price_metric_in ? f.price_metric_in.split(",").filter(Boolean) : [];
  // Cinch's `status` filter is single-valued (active | archived | all). Model it
  // as a two-option checkbox: none = active-only (backend default), one = that
  // state, both = all.
  const statusSel =
    f.status === "all" ? ["active", "archived"] : f.status ? [f.status] : [];
  const setStatus = (vals: string[]) =>
    st.setFilters({ status: vals.length === 2 ? "all" : (vals[0] ?? undefined) });
  const columns: DataTableColumn<ItemRow>[] = [
    {
      key: "name",
      header: "Item",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openRow({ kind: "items", id: r.id, row: asRow(r) }, { expanded: true })}
        />
      ),
    },
    { key: "category", header: "Category", sortable: true, render: (r) => categoryLabel(r.category) },
    {
      key: "integration_keys",
      header: "Integrations",
      render: (r) => (r.integration_keys?.length ? integrationLabels(r.integration_keys) : "—"),
    },
    { key: "price_metric_name", header: "Price Metric", render: (r) => r.price_metric_name || "—" },
    { key: "unit_cost", header: "Unit Cost", numeric: true, render: (r) => fmtCur(r.unit_cost) },
    { key: "floor_price", header: "Floor", numeric: true, render: (r) => fmtCur(r.floor_price) },
    { key: "starting_price", header: "Starting", numeric: true, render: (r) => fmtCur(r.starting_price) },
    {
      key: "recommended_price",
      header: "Recommended",
      numeric: true,
      render: (r) => fmtCur(r.recommended_price),
    },
  ];
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      render: (r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          archived={!!r.is_archived}
          onToggleArchive={() => archive.mutate({ id: r.id, archived: !r.is_archived })}
        />
      ),
    });
  }
  return (
    <div className="space-y-3">
      <FilterBar>
        <MultiSelectChip
          label="Category"
          options={ITEM_CATEGORY_OPTIONS}
          value={category}
          onChange={(v) => st.setFilters({ category_in: v.join(",") })}
        />
        <MultiSelectChip
          label="Status"
          options={ARCHIVE_STATUS_OPTIONS}
          value={statusSel}
          onChange={setStatus}
        />
        <MultiSelectChip
          label="Price Metric"
          options={metricOptions}
          value={priceMetric}
          onChange={(v) => st.setFilters({ price_metric_in: v.join(",") })}
        />
        <div className="flex-1" />
        {Object.keys(f).length > 0 && <FilterClearAll onClick={st.clearFilters} />}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={q.data?.results ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav.openRow({ kind: "items", id: r.id, row: asRow(r) })}
        selectedKey={nav.selectedId}
        sort={st.sort}
        onSortChange={st.setSort}
        search={st.search}
        onSearchChange={st.setSearch}
        searchPlaceholder="Search items…"
        pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
        onPageChange={st.setOffset}
        loading={q.isLoading}
        error={errMsg(q.error)}
        emptyMessage="No items."
        toolbar={canWrite ? <NewButton label="New Item" onClick={() => setEditing(null)} /> : undefined}
      />
      <ItemEditor open={editing !== undefined} item={editing ?? null} onClose={() => setEditing(undefined)} />
    </div>
  );
}

/** Toolbar "New …" button shown to contracts.write users on editable tabs. */
function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick}>
      <Plus className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

/** Per-row Edit + Archive/Unarchive controls. stopPropagation so they don't also
 *  open the read drawer via the row's onClick. */
function RowActions({
  onEdit,
  archived,
  onToggleArchive,
}: {
  onEdit: () => void;
  archived: boolean;
  onToggleArchive: () => void;
}) {
  return (
    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      <Button variant="ghost" size="icon" aria-label="Edit" onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={archived ? "Unarchive" : "Archive"}
        title={archived ? "Unarchive" : "Archive"}
        onClick={onToggleArchive}
      >
        {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

function PackagesTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = usePackages(st.params);
  const canWrite = useCan("contracts.write");
  const [editing, setEditing] = useState<PackageRow | null | undefined>(undefined);
  const { archive } = usePackageWrites();
  const columns: DataTableColumn<PackageRow>[] = [
    {
      key: "name",
      header: "Package",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openRow({ kind: "packages", id: r.id, row: asRow(r) }, { expanded: true })}
        />
      ),
    },
    { key: "sku", header: "SKU", render: (r) => r.sku || "—" },
    { key: "pricing_type", header: "Pricing", render: (r) => pricingTypeLabel(r.pricing_type) },
    { key: "price", header: "Price", numeric: true, sortable: true, render: (r) => fmtCur(r.price) },
    { key: "estimated_gross_margin", header: "Est. Margin", numeric: true, render: (r) => marginPct(r.estimated_gross_margin) },
    {
      key: "proposal_eligible",
      header: "Proposal",
      render: (r) => (r.proposal_eligible ? <StatusBadge value="eligible" /> : "—"),
    },
    { key: "package_items", header: "Items", numeric: true, render: (r) => r.package_items?.length ?? 0 },
  ];
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      render: (r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          archived={!!r.is_archived}
          onToggleArchive={() => archive.mutate({ id: r.id, archived: !r.is_archived })}
        />
      ),
    });
  }
  return (
    <div className="space-y-3">
      <DataTable
        columns={columns}
        rows={q.data?.results ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav.openRow({ kind: "packages", id: r.id, row: asRow(r) })}
        selectedKey={nav.selectedId}
        sort={st.sort}
        onSortChange={st.setSort}
        search={st.search}
        onSearchChange={st.setSearch}
        searchPlaceholder="Search packages…"
        pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
        onPageChange={st.setOffset}
        loading={q.isLoading}
        error={errMsg(q.error)}
        emptyMessage="No packages."
        toolbar={canWrite ? <NewButton label="New Package" onClick={() => setEditing(null)} /> : undefined}
      />
      <PackageEditor open={editing !== undefined} pkg={editing ?? null} onClose={() => setEditing(undefined)} />
    </div>
  );
}

function DiscountsTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = useDiscounts(st.params);
  const canWrite = useCan("contracts.write");
  const [editing, setEditing] = useState<DiscountRow | null | undefined>(undefined);
  const { archive } = useDiscountWrites();
  const f = st.filters;
  const type = f.type ? f.type.split(",").filter(Boolean) : [];
  const columns: DataTableColumn<DiscountRow>[] = [
    {
      key: "name",
      header: "Discount",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openRow({ kind: "discounts", id: r.id, row: asRow(r) }, { expanded: true })}
        />
      ),
    },
    { key: "type", header: "Type", render: (r) => discountTypeLabel(r.type) },
    { key: "value", header: "Value", render: (r) => discountValueLabel(r) },
    { key: "code", header: "Code", render: (r) => r.code || "—" },
    { key: "stackable", header: "Stackable", render: (r) => (r.stackable ? "Yes" : "No") },
  ];
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      render: (r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          archived={!!r.is_archived}
          onToggleArchive={() => archive.mutate({ id: r.id, archived: !r.is_archived })}
        />
      ),
    });
  }
  return (
    <div className="space-y-3">
      <FilterBar>
        <MultiSelectChip
          label="Type"
          options={DISCOUNT_TYPE_OPTIONS}
          value={type}
          onChange={(v) => st.setFilters({ type: v.join(",") })}
        />
        <div className="flex-1" />
        {Object.keys(f).length > 0 && <FilterClearAll onClick={st.clearFilters} />}
      </FilterBar>
      <DataTable
        columns={columns}
        rows={q.data?.results ?? []}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav.openRow({ kind: "discounts", id: r.id, row: asRow(r) })}
        selectedKey={nav.selectedId}
        sort={st.sort}
        onSortChange={st.setSort}
        search={st.search}
        onSearchChange={st.setSearch}
        searchPlaceholder="Search discounts…"
        pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
        onPageChange={st.setOffset}
        loading={q.isLoading}
        error={errMsg(q.error)}
        emptyMessage="No discounts."
        toolbar={canWrite ? <NewButton label="New Discount" onClick={() => setEditing(null)} /> : undefined}
      />
      <DiscountEditor open={editing !== undefined} discount={editing ?? null} onClose={() => setEditing(undefined)} />
    </div>
  );
}

function ContractTypesTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = useContractTypes(st.params);
  const columns: DataTableColumn<ContractTypeRow>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openDetail("contract-types", r.id, { expanded: true })}
        />
      ),
    },
    { key: "descr", header: "Description", render: (r) => r.descr || "—" },
    { key: "contract_count", header: "Contracts", numeric: true, render: (r) => r.contract_count ?? 0 },
  ];
  return (
    <DataTable
      columns={columns}
      rows={q.data?.results ?? []}
      rowKey={(r) => r.id}
      onRowClick={(r) => nav.openDetail("contract-types", r.id)}
      selectedKey={nav.selectedId}
      sort={st.sort}
      onSortChange={st.setSort}
      search={st.search}
      onSearchChange={st.setSearch}
      searchPlaceholder="Search contract types…"
      pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
      onPageChange={st.setOffset}
      loading={q.isLoading}
      error={errMsg(q.error)}
      emptyMessage="No contract types."
    />
  );
}

function PriceMetricsTab({ nav }: { nav: TabNav }) {
  const st = useTableState({ key: "name", dir: "asc" });
  const q = usePriceMetrics(st.params);
  const columns: DataTableColumn<PriceMetricRow>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (r) => (
        <NameCell
          label={r.name || "—"}
          onExpand={() => nav.openRow({ kind: "price-metrics", id: r.id, row: asRow(r) }, { expanded: true })}
        />
      ),
    },
    { key: "description", header: "Description", render: (r) => r.description || "—" },
    {
      key: "support_tiers",
      header: "Support Tiers",
      render: (r) => (r.support_tiers ? "Yes" : "No"),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={q.data?.results ?? []}
      rowKey={(r) => r.id}
      onRowClick={(r) => nav.openRow({ kind: "price-metrics", id: r.id, row: asRow(r) })}
      selectedKey={nav.selectedId}
      sort={st.sort}
      onSortChange={st.setSort}
      search={st.search}
      onSearchChange={st.setSearch}
      searchPlaceholder="Search price metrics…"
      pagination={{ limit: st.limit, offset: st.offset, count: q.data?.count ?? 0 }}
      onPageChange={st.setOffset}
      loading={q.isLoading}
      error={errMsg(q.error)}
      emptyMessage="No price metrics."
    />
  );
}
